import { memo } from 'react';

import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import { getByPath } from '@/utils/widgets';
import type { WidgetData } from '../../widget.type';
import type { LabelsView } from './labels.type';

type Props = {
  view: LabelsView;
  data: WidgetData;
};

const LabelsViewImpl = memo(({ view, data }: Props) => {
  const { path } = view;

  const labels = getByPath<string | string[]>(data, path);
  if (!labels) return null;

  const finalLabels = Array.isArray(labels) ? labels : [labels];

  return (
    <div className="scrollbar-thin flex h-full w-full flex-row flex-nowrap items-center gap-px overflow-x-auto overflow-y-hidden">
      {finalLabels.map((label, index) => (
        <div
          className="m-px h-[18px] shrink-0 truncate rounded-sm border-[0.5px] border-border bg-accent px-[5px] py-[2px] text-[11px] text-accent-foreground leading-[14px]"
          key={index}
        >
          {label}
        </div>
      ))}
    </div>
  );
});

// sort defaults to length on path
const getSortConfig = (view: LabelsView) => ({
  type: 'length',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: LabelsView) => ({
  type: 'text',
  path: view.path
});

// width defaults to 'large'
const getDefaultWidth = (): ColumnWidth => 'large';

// stringifies data at path
const getStringifyFunction = (view: LabelsView) => {
  const { path } = view;
  return (data: WidgetData) => {
    const labels = getByPath(data, path);
    if (!labels) return '';

    const finalLabels = Array.isArray(labels) ? labels : [labels];

    return finalLabels.join(', ');
  };
};

export const labelsViewProvider = {
  component: LabelsViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
